import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiChevronRight, FiEdit2, FiList } from 'react-icons/fi';
import { apiService } from '../../components/Config/api.service';
import { ListeEquipe, Organigramme } from '../../components/Equipe/VueEquipe';

/**
 * L'onglet « Équipe » de la fiche collaborateur, côté RH (Yros/Kavero#9,
 * #473, contrat C31). La chaîne au-dessus, les N-1 et le département ; chaque
 * nom ouvre la fiche de la personne, sur ce même onglet : on descend dans
 * l'organigramme étage par étage. Deux raccourcis mènent aux outils
 * existants, sans nouveau moyen d'écrire.
 *
 * Yros/Kavero#494 : le responsable et la personne, puis ses N-1, se lisent
 * comme dans « Mon équipe » (`components/Equipe/VueEquipe`) : lignes
 * compactes, filtres par statut du jour, recherche au-delà de quelques N-1.
 */

async function enveloppeDe(appel) {
  try {
    return (await appel()) || {};
  } catch (erreur) {
    return erreur?.response?.status === 409 ? erreur.response.data || {} : {};
  }
}

const BOUTON_SECONDAIRE =
  'inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-[--color-primary] ' +
  'text-[--color-primary] hover:bg-[--color-primary] hover:text-white transition-colors duration-200';

function EquipeCollaborateur({ employeId, onModifier }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [donnees, setDonnees] = useState(null);
  // `null` : pas d'erreur ; une chaîne vide : erreur sans message du serveur.
  const [erreur, setErreur] = useState(null);

  // Ne dépend que du salarié : dépendre de `t` relancerait la lecture à
  // chaque rendu où la fonction de traduction change.
  useEffect(() => {
    let actif = true;
    setDonnees(null);
    setErreur(null);
    enveloppeDe(() => apiService.lireEquipeCollaborateur(employeId)).then((reponse) => {
      if (!actif) return;
      if (reponse.ok) setDonnees(reponse);
      else setErreur(reponse.message || '');
    });
    return () => { actif = false; };
  }, [employeId]);

  const ouvrir = (id) => navigate(`/employees/${id}?tab=equipe`);
  const voirDansLaListe = () => {
    const filtre = { filters: [{
      field: 'parent_id', operator: '=', value: employeId,
      label: t('equipeCollaborateur.filtreEquipe', { nom: donnees.salarie.nom }) }] };
    navigate(`/employees?filter_by=${encodeURIComponent(JSON.stringify(filtre))}`);
  };

  if (erreur !== null) {
    return (
      <p role="alert" className="m-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        {erreur || t('equipeCollaborateur.erreur')}
      </p>
    );
  }
  if (!donnees) {
    return <p className="m-4 text-sm text-gray-500">{t('equipeCollaborateur.chargement')}</p>;
  }

  const { salarie, chaine, equipe, departement } = donnees;
  return (
    <div className="p-4 space-y-6 text-sm">
      <section>
        <h3 className="mb-2 font-semibold text-gray-700">{t('equipeCollaborateur.auDessus')}</h3>
        {chaine.length === 0 ? (
          <p className="text-gray-500">{t('equipeCollaborateur.aucunResponsable')}</p>
        ) : (
          <nav aria-label={t('equipeCollaborateur.auDessus')} className="flex flex-wrap items-center gap-1">
            {chaine.map((personne) => (
              <React.Fragment key={personne.id}>
                <button type="button" className="text-[--color-primary] hover:underline" onClick={() => ouvrir(personne.id)}>
                  {personne.nom}
                </button>
                <FiChevronRight className="text-gray-400" aria-hidden="true" />
              </React.Fragment>
            ))}
            <span className="font-semibold text-gray-800">{salarie.nom}</span>
          </nav>
        )}
      </section>

      <section className="rounded-xl border border-gray-100 bg-white p-4">
        <Organigramme responsable={chaine[chaine.length - 1] || null} moi={salarie} onOuvrir={ouvrir}
          libelleResponsable={t('equipeCollaborateur.responsable')} />
      </section>

      <section>
        <h3 className="mb-2 font-semibold text-gray-700">{t('equipeCollaborateur.sonEquipe', { n: equipe.length })}</h3>
        {equipe.length === 0 ? (
          <p className="text-gray-500">{t('equipeCollaborateur.aucuneEquipe')}</p>
        ) : (
          <ListeEquipe equipe={equipe} onOuvrir={ouvrir} />
        )}
      </section>

      {departement && (
        <section>
          <h3 className="mb-2 font-semibold text-gray-700">{t('equipeCollaborateur.departement')}</h3>
          <p className="text-gray-700">
            {departement.nom}
            {departement.responsable && (
              <>
                {' · '}{t('equipeCollaborateur.responsableDepartement')}{' '}
                <button type="button" className="text-[--color-primary] hover:underline"
                  onClick={() => ouvrir(departement.responsable.id)}>
                  {departement.responsable.nom}
                </button>
              </>
            )}
          </p>
        </section>
      )}

      <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
        <button type="button" className={BOUTON_SECONDAIRE} onClick={onModifier}>
          <FiEdit2 aria-hidden="true" /> {t('equipeCollaborateur.changerResponsable')}
        </button>
        {equipe.length > 0 && (
          <button type="button" className={BOUTON_SECONDAIRE} onClick={voirDansLaListe}>
            <FiList aria-hidden="true" /> {t('equipeCollaborateur.voirDansLaListe')}
          </button>
        )}
      </div>
    </div>
  );
}

export default EquipeCollaborateur;
